import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const RECOVERABLE_IMPORT_ERROR = /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryKey: number;
}

export class LazyErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, retryKey: 0 };

  private isRecoverableImportError(error: Error | null) {
    if (!error) return false;
    return RECOVERABLE_IMPORT_ERROR.test(error.message);
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  handleRetry = () => {
    if (typeof window !== 'undefined') {
      // Always hard-reload for chunk errors to bust stale caches
      if (this.isRecoverableImportError(this.state.error)) {
        window.location.reload();
        return;
      }

      const retryUrl = new URL(window.location.href);
      const retryCount = Number(retryUrl.searchParams.get('__lazy_retry') ?? '0');

      if (retryCount < 2) {
        retryUrl.searchParams.set('__lazy_retry', String(retryCount + 1));
        retryUrl.searchParams.set('__retry', Date.now().toString());
        window.location.replace(retryUrl.toString());
        return;
      }
    }

    this.setState((state) => ({
      hasError: false,
      error: null,
      retryKey: state.retryKey + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      const isRecoverableImportError = this.isRecoverableImportError(this.state.error);

      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>
          <h2 className="text-lg font-semibold">Failed to load this page</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {isRecoverableImportError
              ? 'A temporary loading error occurred. Refresh to sync the latest files and try again.'
              : 'An unexpected error occurred. Click retry to try again.'}
          </p>
          <Button onClick={this.handleRetry} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {isRecoverableImportError ? 'Refresh page' : 'Retry'}
          </Button>
        </div>
      );
    }

    return <div key={this.state.retryKey}>{this.props.children}</div>;
  }
}
